import { useEffect, useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { EmptyState } from '../../components/Pick/ui'
import ModaleUtenti from './ModaleUtenti'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import Ionicons from '@expo/vector-icons/Ionicons'
export default function RiepilogoPick({ userId, raceId, leagueId }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const [myRows, setMyRows] = useState([])
  const [members, setMembers] = useState([])
  const [compare, setCompare] = useState([])
  const [compareData, setCompareData] = useState({})
  const [loading, setLoading] = useState(true)
  const [loadingCmp, setLoadingCmp] = useState(false)
  const [showModaleUtenti, setShowModaleUtenti] = useState(false)
  const [layoutMode, setLayoutMode] = useState('tappa')
  const [allRidersRace, setAllRidersRace] = useState([])
  const [selectedRiders, setSelectedRiders] = useState([])
  const [showModaleRiders, setShowModaleRiders] = useState(false)
  const [matrixData, setMatrixData] = useState({})
  const [loadingMatrix, setLoadingMatrix] = useState(false)
  const MAX_COMPARE = 3

  useEffect(() => {
    if (!userId || !raceId || !leagueId) return
    let cancelled = false
    const load = async () => {
      try {
        const { data, error } = await supabase.rpc('fn_check_duplicate_tour', { p_id_user: userId, p_id_race: raceId, p_id_league: leagueId })
        if (error) throw error
        if (!cancelled) { setMyRows(data ?? []); setLoading(false) }
      } catch { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [userId, raceId, leagueId])

  useEffect(() => {
    if (!leagueId) return
    const load = async () => {
      const { data } = await supabase.rpc('fn_league_members', { p_id_league: leagueId })
      setMembers(data ?? [])
    }
    load()
  }, [leagueId])

  useEffect(() => {
    if (!raceId) return
    const load = async () => {
      const { data } = await supabase.from('view_start_list_display').select('id_rider, rider_name, rider_surname').eq('id_race', raceId).order('rider_name')
      setAllRidersRace(data ?? [])
    }
    load()
  }, [raceId])

  useEffect(() => {
    if (selectedRiders.length === 0 || !leagueId || !raceId) { setMatrixData({}); return }
    let cancelled = false
    const load = async () => {
      setLoadingMatrix(true)
      try {
        const riderIds = selectedRiders.map(r => r.id_rider)

        // Solo tappe concluse
        const { data: finishedStages } = await supabase
          .from('view_stage_all_status')
          .select('id_stage')
          .eq('id_race', raceId)
          .eq('stage_status', 'finished')

        const finishedStageIds = (finishedStages ?? []).map(s => s.id_stage)
        if (finishedStageIds.length === 0) {
          if (!cancelled) setMatrixData({})
          return
        }

        const { data } = await supabase
          .from('fact_user_pick')
          .select('id_user, id_rider')
          .eq('id_race', raceId)
          .eq('id_league', leagueId)
          .in('id_rider', riderIds)
          .in('id_stage', finishedStageIds)

        if (cancelled) return
        const map = {}
          ; (data ?? []).forEach(p => { if (!map[p.id_user]) map[p.id_user] = new Set(); map[p.id_user].add(p.id_rider) })
        setMatrixData(map)
      } catch { if (!cancelled) setMatrixData({}) }
      finally { if (!cancelled) setLoadingMatrix(false) }
    }
    load()
    return () => { cancelled = true }
  }, [selectedRiders, leagueId, raceId])

  useEffect(() => {
    if (compare.length === 0) { setCompareData({}); return }
    let cancelled = false
    const load = async () => {
      setLoadingCmp(true)
      try {
        const results = await Promise.all(
          compare.map(async uid => {
            const { data } = await supabase.rpc('fn_check_duplicate_tour', { p_id_user: uid, p_id_race: raceId, p_id_league: leagueId })
            return { uid, rows: (data ?? []).filter(r => r.stage_status === 'finished') }
          })
        )
        if (cancelled) return
        const map = {}
        results.forEach(({ uid, rows }) => { map[uid] = rows })
        setCompareData(map)
      } catch { if (!cancelled) setCompareData({}) }
      finally { if (!cancelled) setLoadingCmp(false) }
    }
    load()
    return () => { cancelled = true }
  }, [compare, raceId, leagueId])

  const toggleCompare = (uid) => {
    setCompare(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : prev.length < MAX_COMPARE ? [...prev, uid] : prev)
  }

  const allStages = [...new Set([
    ...myRows.map(r => r.id_stage_number),
    ...Object.values(compareData).flat().map(r => r.id_stage_number)
  ])].sort((a, b) => a - b)

  const myMap = {}
  myRows.forEach(r => {
    if (!myMap[r.id_stage_number]) myMap[r.id_stage_number] = []
    myMap[r.id_stage_number].push({ name: r.rider_name, status: r.stage_status, slot: r.id_slot })
  })

  const cmpMaps = {}
  compare.forEach(uid => {
    cmpMaps[uid] = {}
      ; (compareData[uid] ?? []).forEach(r => {
        if (!cmpMaps[uid][r.id_stage_number]) cmpMaps[uid][r.id_stage_number] = []
        cmpMaps[uid][r.id_stage_number].push({ name: r.rider_name, slot: r.id_slot })
      })
  })

  const compareNames = compare.map(uid => members.find(m => m.id_user === uid)?.display_name ?? '—')

  if (loading) return <View style={{ padding: 16 }}><Text style={{ color: tc.textMuted, fontSize: 13 }}>{t('common.loading')}</Text></View>
  if (myRows.length === 0) return <EmptyState icon="📋" title={t('pick.no_picks_riepilogo')} subtitle={t('pick.no_picks_riepilogo_sub')} />

  return (
    <View>
      {showModaleUtenti && (
        <ModaleUtenti members={members.filter(m => m.id_user !== userId)} selected={compare} onToggle={toggleCompare} onClose={() => setShowModaleUtenti(false)} maxSelect={MAX_COMPARE} />
      )}
      {showModaleRiders && (
        <ModaleUtenti
          members={allRidersRace.map(r => ({ id_user: r.id_rider, display_name: r.rider_name }))}
          selected={selectedRiders.map(r => r.id_rider)}
          onToggle={(id) => setSelectedRiders(prev =>
            prev.find(r => r.id_rider === id) ? prev.filter(r => r.id_rider !== id) : prev.length < 5 ? [...prev, allRidersRace.find(r => r.id_rider === id)] : prev
          )}
          onClose={() => setShowModaleRiders(false)}
          maxSelect={5}
        />
      )}

      {/* Tab switcher */}
      <View style={styles.tabRow}>
        {[
          ['tappa', 'list-outline', t('pick.tab_per_tappa')],
          ['matrice', 'people-circle-outline', t('pick.tab_matrice')],
        ].map(([key, icon, label]) => (
          <TouchableOpacity key={key} onPress={() => setLayoutMode(key)} style={[styles.tabBtn, layoutMode === key && styles.tabBtnActive]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Ionicons name={icon} size={14} color={layoutMode === key ? tc.text : tc.textMuted} />
              <Text style={[styles.tabText, layoutMode === key && styles.tabTextActive]}>{label}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* ── LAYOUT TAPPA ── */}
      {layoutMode === 'tappa' && (
        <View>
          {/* Bottone confronto */}
          <TouchableOpacity style={styles.compareBtn} onPress={() => setShowModaleUtenti(true)}>
            <Text style={styles.compareBtnText}>
              {compare.length > 0 ? t('pick.compare_with', { names: compareNames.join(', ') }) : t('pick.compare_default', { max: MAX_COMPARE })}
            </Text>
            {compare.length > 0 && <Text style={styles.compareBtnHint}>{t('pick.tap_to_change')}</Text>}
          </TouchableOpacity>

          {/* Tabella */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={{ minWidth: 300 }}>
              {/* Header */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View style={styles.cellStage}><Text style={styles.headerText}>T</Text></View>
                <View style={styles.cellPick}><Text style={[styles.headerText, { color: tc.rosaText }]}>{t('pick.you')}</Text></View>
                {compare.map(uid => (
                  <View key={uid} style={styles.cellPick}>
                    <Text style={styles.headerText}>{members.find(m => m.id_user === uid)?.display_name ?? '—'}</Text>
                  </View>
                ))}
              </View>

              {/* Righe */}
              {allStages.map(stageNum => {
                const myPicks = myMap[stageNum] ?? []
                const isFuture = myPicks.some(p => p.status === 'upcoming')
                const isLive = myPicks.some(p => p.status === 'live')
                return (
                  <View key={stageNum} style={[styles.tableRow, isFuture && { opacity: 0.5 }]}>
                    <View style={styles.cellStage}>
                      <Text style={[styles.stageNum, { color: isLive ? '#5de09a' : isFuture ? tc.textMuted : tc.rosaText }]}>{stageNum}</Text>
                      {isLive && <View style={styles.liveDot} />}
                    </View>
                    <View style={styles.cellPick}>
                      {myPicks.sort((a, b) => a.slot - b.slot).map((p, i) => (
                        <Text key={i} style={[styles.pickName, isFuture && { color: tc.textMuted, fontStyle: 'italic' }]} numberOfLines={1}>
                          {isFuture && '🕐 '}{p.name}
                        </Text>
                      ))}
                      {myPicks.length === 0 && <Text style={styles.pickEmpty}>—</Text>}
                    </View>
                    {compare.map(uid => {
                      const cmpPicks = (cmpMaps[uid]?.[stageNum] ?? []).sort((a, b) => a.slot - b.slot)
                      return (
                        <View key={uid} style={styles.cellPick}>
                          {loadingCmp ? <Text style={styles.pickEmpty}>...</Text>
                            : cmpPicks.length > 0 ? cmpPicks.map((p, i) => <Text key={i} style={styles.pickNameMuted} numberOfLines={1}>{p.name}</Text>)
                              : <Text style={styles.pickEmpty}>—</Text>}
                        </View>
                      )
                    })}
                  </View>
                )
              })}
            </View>
          </ScrollView>
        </View>
      )}

      {/* ── LAYOUT MATRICE ── */}
      {layoutMode === 'matrice' && (
        <View>
          <TouchableOpacity style={styles.compareBtn} onPress={() => setShowModaleRiders(true)}>
            <Text style={styles.compareBtnText}>
              🚴 {selectedRiders.length > 0 ? selectedRiders.map(r => r.rider_surname ?? r.rider_name).join(', ') : t('pick.matrix_select_riders')}
            </Text>
            {selectedRiders.length > 0 && <Text style={styles.compareBtnHint}>{t('pick.tap_to_change')}</Text>}
          </TouchableOpacity>

          {selectedRiders.length === 0 ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <Text style={{ fontSize: 13, color: tc.textMuted, textAlign: 'center' }}>{t('pick.matrix_hint')}</Text>
            </View>
          ) : loadingMatrix ? (
            <View style={{ padding: 16 }}><Text style={{ color: tc.textMuted, fontSize: 13 }}>{t('common.loading')}</Text></View>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View>
                {/* Header */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <View style={{ width: 100 }}><Text style={styles.headerText}>{t('pick.user_col')}</Text></View>
                  {selectedRiders.map(r => (
                    <View key={r.id_rider} style={{ width: 70, alignItems: 'center' }}>
                      <Text style={[styles.headerText, { color: tc.rosaText, textAlign: 'center' }]} numberOfLines={2}>{r.rider_surname ?? r.rider_name}</Text>
                    </View>
                  ))}
                </View>
                {/* Righe utenti */}
                {members.sort((a, b) => a.display_name.localeCompare(b.display_name)).map(m => {
                  const isMe = m.id_user === userId
                  const userPicks = matrixData[m.id_user] ?? new Set()
                  return (
                    <View key={m.id_user} style={[styles.tableRow, isMe && { backgroundColor: tc.rosaSubtle }]}>
                      <View style={{ width: 100 }}>
                        <Text style={[styles.pickName, isMe && { fontFamily: fonts.condensedBold, color: tc.rosaText }]} numberOfLines={1}>{m.display_name}</Text>
                      </View>
                      {selectedRiders.map(r => {
                        const hasPicked = userPicks.has(r.id_rider)
                        return (
                          <View key={r.id_rider} style={{ width: 70, alignItems: 'center' }}>
                            <Text style={{ fontSize: 14, opacity: hasPicked ? 1 : 0.25 }}>{hasPicked ? '✅' : '❌'}</Text>
                          </View>
                        )
                      })}
                    </View>
                  )
                })}
              </View>
            </ScrollView>
          )}
        </View>
      )}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  tabRow: { flexDirection: 'row', gap: 6, marginBottom: 16 },
  tabBtn: { flex: 1, padding: 8, borderRadius: radius.md, backgroundColor: tc.bgCard2, borderWidth: 2, borderColor: 'transparent', alignItems: 'center' },
  tabBtnActive: { backgroundColor: tc.bgCard, borderColor: tc.rosa },
  tabText: { fontSize: 12, fontFamily: fonts.condensed, color: tc.textMuted },
  tabTextActive: { color: tc.text },
  compareBtn: { width: '100%', padding: 10, borderRadius: radius.md, backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.rosaBorder, alignItems: 'center', marginBottom: 12, flexDirection: 'row', justifyContent: 'center', gap: 8 },
  compareBtnText: { fontSize: 13, fontFamily: fonts.condensed, color: tc.rosaText },
  compareBtnHint: { fontSize: 11, color: tc.textMuted, opacity: 0.7 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: tc.border },
  tableHeader: { borderBottomWidth: 2, borderBottomColor: tc.border },
  headerText: { fontFamily: fonts.condensedBold, fontSize: 11, color: tc.textMuted, textTransform: 'uppercase', letterSpacing: 1, padding: 8, paddingHorizontal: 12 },
  cellStage: { width: 36, alignItems: 'center', justifyContent: 'center', padding: 8 },
  cellPick: { flex: 1, minWidth: 120, padding: 8, paddingHorizontal: 12 },
  stageNum: { fontFamily: fonts.condensedBold, fontSize: 15 },
  liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success, marginTop: 2 },
  pickName: { fontSize: 13, color: tc.text, lineHeight: 20 },
  pickNameMuted: { fontSize: 13, color: tc.textMuted, lineHeight: 20 },
  pickEmpty: { fontSize: 12, color: tc.textMuted },
})